import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { RenderedOutputSourceArtifactSlim } from 'situview-webapi-client';
import { CompanyService } from 'src/app/services/company.service';

@Component({
  selector: 'source-artifacts-menu',
  templateUrl: './source-artifacts-menu.component.html',
  styleUrls: ['./source-artifacts-menu.component.less']
})
export class SourceArtifactsMenuComponent implements OnChanges, OnDestroy {
  /* standard */
  @Input() isAdmin: boolean = false;
  @Input() isSituUser: boolean = false;
  @Input() isDarkMode: boolean = false;
  
  @Input() sourceArtifacts: RenderedOutputSourceArtifactSlim[] | undefined;
  @Input() secondSourceArtifacts: RenderedOutputSourceArtifactSlim[] | undefined;
  @Input() colourOverride: string | undefined;
  @Input() accessoryCodes: string[] | undefined;
  filteredArtifacts: GroupedArtifacts[] = [];
  companyIdsByCode = new Map<string, string>();

  private companiesSubscription: Subscription;

  constructor(companyService: CompanyService) {
    this.companiesSubscription = companyService.companies$.subscribe(companies => {
      this.companyIdsByCode = new Map((companies ?? [])
        .filter(company => company.code && company.id)
        .map(company => [company.code!.toLowerCase(), company.id!]));
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.sourceArtifacts?.currentValue || changes?.accessoryCodes) {
      this.filterAndGroupArtifacts();
    }
  }

  ngOnDestroy() {
    this.companiesSubscription.unsubscribe();
  }

  filterAndGroupArtifacts(): void {
    if (!this.sourceArtifacts) return;

    const filteredArtifacts = this.sourceArtifacts.filter(artifact => !!artifact.callerReferenceKey);

    const groupedArtifacts: { [key: string]: RenderedOutputSourceArtifactSlimExtended[] } = {};

    filteredArtifacts.forEach((artifact: RenderedOutputSourceArtifactSlim) => {
      let groupKey = '';
      let modifiedCallerReferenceKey = artifact.callerReferenceKey!;
  
      if (artifact.callerReferenceKey?.startsWith('software\\')) {
        groupKey = 'software';
        modifiedCallerReferenceKey = artifact.callerReferenceKey.substr('software\\'.length);
      } else if (artifact.callerReferenceKey?.startsWith('ifc\\')) {
        groupKey = this.isAccessoryArtifact(artifact.callerReferenceKey) ? 'accessories' : 'ifc';
        const secondBackslashIndex = artifact.callerReferenceKey.indexOf('\\', artifact.callerReferenceKey.indexOf('\\') + 1);
        modifiedCallerReferenceKey = artifact.callerReferenceKey.substr(secondBackslashIndex + 1);
      } else if (artifact.callerReferenceKey?.startsWith('assetmodule\\')) {
        groupKey = 'themes';
        const lastBackslashIndex = artifact.callerReferenceKey.lastIndexOf('\\');
        modifiedCallerReferenceKey = artifact.callerReferenceKey.substr(lastBackslashIndex + 1);
      } else {
        groupKey = 'other';
        if (artifact.callerReferenceKey) {
          if (artifact.callerReferenceKey?.startsWith('placement\\')) {
            const lastBackslashIndex = artifact.callerReferenceKey.lastIndexOf('\\');
            modifiedCallerReferenceKey = `placement - ${artifact.callerReferenceKey.substr(lastBackslashIndex + 1)}`;
          }
          if (artifact.callerReferenceKey?.startsWith('builderifcsettings\\')) {
            const lastBackslashIndex = artifact.callerReferenceKey.lastIndexOf('\\');
            modifiedCallerReferenceKey = "ifcsettings";
          }
        }
      }
  
      // Clone so the source array stays pristine and grouping can safely re-run
      let extendedArtifact = new RenderedOutputSourceArtifactSlimExtended(artifact);
      extendedArtifact.callerReferenceKey = modifiedCallerReferenceKey;
      extendedArtifact.fullKey = artifact.callerReferenceKey!;
      extendedArtifact.companyCode = this.getCompanyCode(artifact.callerReferenceKey!);
      extendedArtifact.displayText = this.getDisplayedText(groupKey, extendedArtifact);
      extendedArtifact.isClickable = this.isClickable(groupKey);
      extendedArtifact.routes = this.getRouterLink(groupKey, extendedArtifact);
      extendedArtifact.queryParams = this.getQueryParams(groupKey, extendedArtifact);
  
      if (!groupedArtifacts[groupKey]) {
        groupedArtifacts[groupKey] = [];
      }
  
      if (groupKey === 'software' && this.secondSourceArtifacts) {
        const secondArtifact = this.secondSourceArtifacts.find(
          (secondArtifact: RenderedOutputSourceArtifactSlim) =>
            secondArtifact.callerReferenceKey === artifact.callerReferenceKey
        );
  
        if (secondArtifact && secondArtifact.version !== artifact.version) {
          extendedArtifact.isDifferentVersion = true;
        }
      }
  
      groupedArtifacts[groupKey].push(extendedArtifact);
    });
  
    this.filteredArtifacts = [
      { groupKey: 'ifc', artifacts: groupedArtifacts['ifc'] || [] },
      { groupKey: 'accessories', artifacts: groupedArtifacts['accessories'] || [] },
      { groupKey: 'software', artifacts: groupedArtifacts['software'] || [] },
      { groupKey: 'themes', artifacts: groupedArtifacts['themes'] || [] },
      { groupKey: 'other', artifacts: groupedArtifacts['other'] || [] },
    ].filter(group => group.groupKey !== 'accessories' || group.artifacts.length > 0);
  }

  // Accessory codes may be company-prefixed ("company.code"); the ifc key's last segment is the bare file name.
  private isAccessoryArtifact(callerReferenceKey: string): boolean {
    if (!this.accessoryCodes?.length) return false;
    const ifcName = callerReferenceKey.split('\\').pop()?.toLowerCase();
    if (!ifcName) return false;
    return this.accessoryCodes.some(code => code?.toLowerCase().split('.').pop() === ifcName);
  }

  // Every key except software's is `{type}\{companyCode}\...`.
  private getCompanyCode(callerReferenceKey: string): string | undefined {
    const segments = callerReferenceKey.split(/[\\/]/);
    if (segments[0] === 'software' || segments.length < 2) return undefined;
    return segments[1] || undefined;
  }

  getCompanyId(companyCode: string | undefined): string | undefined {
    return companyCode ? this.companyIdsByCode.get(companyCode.toLowerCase()) : undefined;
  }

  isClickable(groupKey: string): boolean {
    return groupKey === 'ifc' || groupKey === 'accessories';
  }

  getRouterLink(groupKey: string, artifact: RenderedOutputSourceArtifactSlim): any[] {
    if (!artifact?.callerReferenceKey) return [];

    if (groupKey === 'ifc' || groupKey === 'accessories') {
      const home = artifact.callerReferenceKey.split('\\')[0];
      return ['/homes/', home];
    }
    return [];
  }

  getQueryParams(groupKey: string, artifact: RenderedOutputSourceArtifactSlim): any {
    if (!artifact?.callerReferenceKey) return {};

    if (groupKey === 'ifc' || groupKey === 'accessories') {
      const ifcFile = artifact.callerReferenceKey.split('\\')[2];
      return { ifcFile: ifcFile, v: artifact.version };
    }
    return {};
  }

  getDisplayedText(groupKey: string, artifact: RenderedOutputSourceArtifactSlim): string {
    if (!artifact?.callerReferenceKey) return "";

    if (groupKey === 'ifc' || groupKey === 'accessories') {
      return artifact.callerReferenceKey.split('\\')[2];
    }
    return artifact.callerReferenceKey;
  }
}

interface GroupedArtifacts {
  groupKey: string;
  artifacts: RenderedOutputSourceArtifactSlimExtended[];
}

class RenderedOutputSourceArtifactSlimExtended extends RenderedOutputSourceArtifactSlim {
  queryParams: any = {};
  routes: any[] = [];
  isClickable: boolean = false;
  displayText: string = "";
  fullKey: string = "";
  companyCode?: string;
  isDifferentVersion?: boolean = false;
}